import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useContext } from 'react';
import { AppContext } from '../context/context';
import RulesAdminPanel from '../components/rulesEngine/RulesAdminPanel';
import DriversPanel from '../components/rulesEngine/DriversPanel';
import DutiesPanel from '../components/rulesEngine/DutiesPanel';
import VehiclesPanel from '../components/rulesEngine/VehiclesPanel';
import RosteringPanel from '../components/rulesEngine/RosteringPanel';
import { useFeatureFlags } from '../services/featureFlags/useFeatureFlags';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" sx={{ pt: 3, display: value !== index ? 'none' : undefined }}>
      {children}
    </Box>
  );
}

function tabIndexFromParam(tab: string | null, rosteringEnabled: boolean): number {
  switch (tab) {
    case 'drivers':
      return 1;
    case 'vehicles':
      return 2;
    case 'duties':
      return 3;
    case 'rostering':
      return rosteringEnabled ? 4 : 0;
    case 'rules':
    default:
      return 0;
  }
}

/**
 * Rules Engine v1 — available to all signed-in users (temporary open access).
 * Assignment-time blocking: rule sets, drivers, duties.
 * Deep-link: /rules-engine?tab=rostering|vehicles|drivers|duties|rules
 */
export default function RulesEnginePage() {
  const context = useContext(AppContext);
  const userId = context?.userData?.userDetails?.userId || '';
  const { isEnabled } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const rulesEngineEnabled = isEnabled('rules_engine');
  const rosteringEnabled = isEnabled('advance_rostering');
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(() => tabIndexFromParam(requestedTab, rosteringEnabled));

  useEffect(() => {
    setTab(tabIndexFromParam(requestedTab, rosteringEnabled));
  }, [requestedTab, rosteringEnabled]);

  if (!userId || !rulesEngineEnabled) {
    return <Navigate to="/" replace />;
  }

  const onTabChange = (_: React.SyntheticEvent, next: number) => {
    setTab(next);
    const names = ['rules', 'drivers', 'vehicles', 'duties', 'rostering'];
    const name = names[next] || 'rules';
    setSearchParams(name === 'rules' ? {} : { tab: name }, { replace: true });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }} data-testid="rules.engine.page">
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Rules Engine
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Plan-time assignment blocking for drivers and vehicles. Soft rules can be overridden with a reason.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={onTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Rule sets" data-testid="rules.engine.tab.rules" />
          <Tab label="Drivers" data-testid="rules.engine.tab.drivers" />
          <Tab label="Vehicles" data-testid="rules.engine.tab.vehicles" />
          <Tab label="Duties" data-testid="rules.engine.tab.duties" />
          {rosteringEnabled && <Tab label="Rostering" data-testid="rules.engine.tab.rostering" />}
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <RulesAdminPanel />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <DriversPanel />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <VehiclesPanel />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <DutiesPanel />
      </TabPanel>
      {rosteringEnabled && (
        <TabPanel value={tab} index={4}>
          <RosteringPanel />
        </TabPanel>
      )}
    </Box>
  );
}
